/**
 * FeedbackDashboardPage — Platform-level dashboard showing all feedback collected
 * via the floating widget. Super Admin sees all hospitals (with filter); Hospital
 * Admin / HR / IT Admin see only their own hospital.
 *
 * Route: /feedback-dashboard
 */
import { useEffect, useMemo, useState } from 'react';
import { feedbackAPI, hospitalsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageSquare, Star, Building2, Calendar, User as UserIcon, TrendingUp, Filter } from 'lucide-react';
import { toast } from 'sonner';

const StarRow = ({ rating }) => {
  if (!rating) return <span className="text-[10px] text-slate-400">—</span>;
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={`feedback-rating-${rating}`}>
      {[1,2,3,4,5].map(n => <Star key={n} size={12} className={n <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'} />)}
    </div>
  );
};

const FeedbackDashboardPage = () => {
  const { user } = useAuth();
  const [hospitals, setHospitals] = useState([]);
  const [hospitalFilter, setHospitalFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = !!user?.is_super_admin;
  const isAdmin = useMemo(() => {
    if (isSuperAdmin) return true;
    const code = (user?.role_code || '').toLowerCase();
    return ['hospital_admin', 'hr', 'it_admin'].includes(code);
  }, [isSuperAdmin, user]);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    if (isSuperAdmin) {
      hospitalsAPI.list().then(r => setHospitals(r.data || [])).catch(() => {});
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isSuperAdmin, hospitalFilter, moduleFilter, ratingFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (hospitalFilter !== 'all') params.hospital_id = hospitalFilter;
      if (moduleFilter !== 'all') params.module = moduleFilter;
      if (ratingFilter !== 'all') params.rating = Number(ratingFilter);
      const [s, l] = await Promise.all([
        feedbackAPI.summary(hospitalFilter !== 'all' ? { hospital_id: hospitalFilter } : {}),
        feedbackAPI.list(params),
      ]);
      setSummary(s.data);
      setRows(l.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load feedback');
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.subject || '').toLowerCase().includes(q)
      || (r.feedback_text || '').toLowerCase().includes(q)
      || (r.staff_name || '').toLowerCase().includes(q)
      || (r.staff_email || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  if (!isAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="feedback-restricted">
        <Card className="max-w-md w-full text-center">
          <CardHeader><CardTitle className="flex items-center justify-center gap-2"><MessageSquare size={18} /> Restricted</CardTitle></CardHeader>
          <CardContent className="text-sm text-slate-500">
            Only HR / Hospital Admin / IT Admin / Super Admin can view aggregated feedback. Your role doesn't have this access — but you can still submit feedback any time via the floating button at the bottom-right of every page.
          </CardContent>
        </Card>
      </div>
    );
  }

  const modules = Array.from(new Set((summary?.by_module || []).map(m => m.module))).filter(Boolean);

  return (
    <div className="space-y-5" data-testid="feedback-dashboard-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center text-white shadow-md">
            <MessageSquare size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Feedback Dashboard</h1>
            <p className="text-sm text-slate-500">All feedback submitted via the floating widget across the platform.</p>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={MessageSquare} color="rose" label="Total Feedback" value={summary?.total ?? '—'} />
        <Kpi icon={TrendingUp} color="emerald" label="Last 7 days" value={summary?.last_7_days ?? '—'} />
        <Kpi icon={Star} color="amber" label="Avg Rating" value={summary?.avg_rating != null ? `${summary.avg_rating} / 5` : '—'} sub={`${summary?.rating_count || 0} rated`} />
        <Kpi icon={Building2} color="blue" label="Hospitals" value={(summary?.by_hospital || []).length} />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          {isSuperAdmin && (
            <div className="min-w-[200px]">
              <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Hospital</p>
              <Select value={hospitalFilter} onValueChange={setHospitalFilter}>
                <SelectTrigger data-testid="feedback-hospital-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All hospitals</SelectItem>
                  {hospitals.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="min-w-[200px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Module</p>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger data-testid="feedback-module-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All modules</SelectItem>
                {modules.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Rating</p>
            <Select value={ratingFilter} onValueChange={setRatingFilter}>
              <SelectTrigger data-testid="feedback-rating-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                {[5,4,3,2,1].map(n => <SelectItem key={n} value={String(n)}>{n} star{n > 1 ? 's' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Search</p>
            <Input placeholder="Search by subject, text, name, email…" value={search} onChange={e => setSearch(e.target.value)} data-testid="feedback-search" />
          </div>
        </CardContent>
      </Card>

      {/* Rating histogram */}
      {summary && summary.rating_count > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Rating Distribution</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 pt-1">
            {[5,4,3,2,1].map(n => {
              const count = summary.by_rating?.[n] || 0;
              const pct = summary.rating_count ? Math.round((count / summary.rating_count) * 100) : 0;
              return (
                <div key={n} className="flex items-center gap-3 text-xs">
                  <span className="w-12 inline-flex items-center gap-1"><Star size={11} className="text-amber-400 fill-amber-400" /> {n}</span>
                  <div className="flex-1 bg-slate-100 rounded h-2 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-amber-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-20 text-right text-slate-600">{count} · {pct}%</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Feedback rows */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Filter size={14} /> {filtered.length} entries</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-slate-400 text-sm">Loading…</div> :
            filtered.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">No feedback matches the current filters.</div> :
            <div className="divide-y divide-slate-100">
              {filtered.map(f => (
                <div key={f.id} className="p-4 hover:bg-slate-50/60 transition" data-testid={`feedback-row-${f.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-bold text-slate-900">{f.subject}</h3>
                        <StarRow rating={f.rating} />
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 font-semibold">{f.module || 'Other'}</span>
                        {f.submodule && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{f.submodule}</span>}
                      </div>
                      <p className="text-xs text-slate-600 mt-1.5 whitespace-pre-wrap leading-relaxed">{f.feedback_text}</p>
                      <div className="mt-2 flex items-center gap-3 flex-wrap text-[10px] text-slate-500">
                        <span className="inline-flex items-center gap-1"><UserIcon size={10} /> {f.staff_name} {f.staff_role && `· ${f.staff_role}`}</span>
                        {f.staff_email && <span>📧 {f.staff_email}</span>}
                        {f.staff_phone && <span>📞 {f.staff_phone}</span>}
                        {isSuperAdmin && f.hospital_name && <span className="inline-flex items-center gap-1"><Building2 size={10} /> {f.hospital_name}</span>}
                        <span className="inline-flex items-center gap-1"><Calendar size={10} /> {new Date(f.submitted_at).toLocaleString('en-IN')}</span>
                        {f.page_url && <span className="font-mono text-[9px]">{f.page_url}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          }
        </CardContent>
      </Card>
    </div>
  );
};

const Kpi = ({ icon: Icon, color, label, value, sub }) => {
  const colorMap = {
    rose: 'bg-rose-100 text-rose-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
  };
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colorMap[color]}`}><Icon size={18} /></div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
          <p className="text-xl font-bold text-slate-900">{value}</p>
          {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

export default FeedbackDashboardPage;
